import React from 'react';
import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Collapse,
    Flex,
    Group,
    NumberInput,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    Title,
    Tooltip,
} from '@mantine/core';
import { IconPlus, IconTrash, IconRefresh, IconX, IconChevronDown, IconArrowBackUp, IconArrowForwardUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { modals } from '@mantine/modals';
import { ITaperingStep, IStepMedication, TimeOfDay } from '../../../global/entities/OTP/OTP';
import { IDoseSuggestionResult } from '../../../global/entities/MME/MME';
import MedicationEditModal from './PrescriptionEditModal';
import Utils from '../../../global/helpers/utils';
import { addCalendarDays, toLocalCalendarDate } from '../../../global/helpers/isoCalendarDate';

interface IDrugRouteOption {
    value: string;
    label: string;
    drugID: number;
    routeID: number;
    unitName: string;
}

interface ITaperingStepsTableProps {
    steps: ITaperingStep[];
    originalMME: number;
    isCustomized: boolean;
    onStepChange: (updatedSteps: ITaperingStep[]) => void;
    onReset: () => void;
    onUndo?: () => void;
    onRedo?: () => void;
    canUndo?: boolean;
    canRedo?: boolean;
    doseSuggestions?: IDoseSuggestionResult[];
    drugRouteOptions?: IDrugRouteOption[];
    globalDrugRouteKey?: string | null;
    disabled?: boolean;
    forceExpanded?: boolean;
    virtualMMEArray?: number[];
}

const TaperingStepsTable: React.FC<ITaperingStepsTableProps> = ({
    steps,
    originalMME,
    isCustomized,
    onStepChange,
    onReset,
    onUndo,
    onRedo,
    canUndo = false,
    canRedo = false,
    doseSuggestions,
    drugRouteOptions,
    globalDrugRouteKey,
    disabled = false,
    forceExpanded,
    virtualMMEArray,
}) => {
    const { t } = useTranslation();
    const hasDrugColumns = (drugRouteOptions && drugRouteOptions.length > 0) ||
        steps.some((s) => s.medications && s.medications.length > 0);

    // Check if any step has a virtual MME difference (to show targeted column on all steps)
    const anyVirtualDiff = React.useMemo(() => {
        if (!virtualMMEArray) return false;
        return steps.some((step, i) => {
            const virtual = virtualMMEArray[i];
            const plannedMME = Math.round(step.doseMME * 2) / 2;
            return virtual !== undefined && Math.abs(virtual - plannedMME) >= 0.01;
        });
    }, [steps, virtualMMEArray]);

    // Collapse state with localStorage persistence
    const [isExpanded, setIsExpanded] = React.useState(() => {
        const saved = localStorage.getItem('otp_steps_expanded');
        return saved !== null ? saved === 'true' : true;
    });

    // Override expanded state when forceExpanded prop changes
    React.useEffect(() => {
        if (forceExpanded !== undefined) 
            setIsExpanded(forceExpanded);
        
    }, [forceExpanded]);

    // Persist collapsed state to localStorage
    React.useEffect(() => {
        localStorage.setItem('otp_steps_expanded', String(isExpanded));
    }, [isExpanded]);

    // State for medication editing modal
    const [editingMedication, setEditingMedication] = React.useState<{
        stepIndex: number;
        medicationIndex: number | null; // null for new medication
        medication?: IStepMedication;
    } | null>(null);

    const recalculateDownstreamSteps = (
        editedSteps: ITaperingStep[],
        changedIndex: number,
    ): ITaperingStep[] => {
        const result = [...editedSteps];

        for (let i = changedIndex; i < result.length; i++) {
            let startDate = result[i].startDate;
            let endDate = result[i].endDate;

            if (i === 0) {
                if (!startDate)
                    startDate = Utils.formatLocalDateToYYYYMMDD(new Date()) ?? '';
                endDate = addCalendarDays(startDate, result[i].durationDays - 1);
            } else {
                const prevEndDate = result[i - 1].endDate;
                startDate = addCalendarDays(prevEndDate, 1);
                endDate = addCalendarDays(startDate, result[i].durationDays - 1);
            }

            result[i] = {
                ...result[i],
                startDate,
                endDate,
                dosePercentageOfOriginal:
                    originalMME > 0
                        ? parseFloat(((result[i].doseMME / originalMME) * 100).toFixed(2))
                        : 0,
                reductionFromPrevious:
                    i > 0
                        ? parseFloat((result[i - 1].doseMME - result[i].doseMME).toFixed(2))
                        : 0,
            };
        }

        return result;
    };

    const handleDurationChange = (index: number, newDuration: number) => {
        if (newDuration < 1) return;
        const updated = steps.map((step, i) =>
            i === index ? { ...step, durationDays: newDuration } : step,
        );
        onStepChange(recalculateDownstreamSteps(updated, index));
    };

    const handleAddStep = () => {
        const lastStep = steps[steps.length - 1];
        const newStepNumber = lastStep ? lastStep.stepNumber + 1 : 1;
        const prevMME = lastStep ? lastStep.doseMME : originalMME;
        const newMME = parseFloat((prevMME * 0.9).toFixed(2));
        const duration = lastStep ? lastStep.durationDays : 7;

        const newStep: ITaperingStep = {
            id: 0,
            stepNumber: newStepNumber,
            startDate: '',
            endDate: '',
            durationDays: duration,
            doseMME: newMME,
            dosePercentageOfOriginal:
                originalMME > 0
                    ? parseFloat(((newMME / originalMME) * 100).toFixed(2))
                    : 0,
            reductionFromPrevious: parseFloat((prevMME - newMME).toFixed(2)),
            medications: [],
        };

        onStepChange([...steps, newStep]);
    };

    const handleRemoveStep = (index: number) => {
        if (steps.length <= 1) return;
        const updated = steps
            .filter((_, i) => i !== index)
            .map((step, i) => ({ ...step, stepNumber: i + 1 }));
        onStepChange(recalculateDownstreamSteps(updated, Math.max(0, index - 1)));
    };

    const formatFrequency = (freq: number): string => {
        switch (freq) {
            case 1: return t('otp.stepsTable.freqOnceDaily', '1x/day');
            case 2: return t('otp.stepsTable.freqTwiceDaily', '2x/day');
            case 3: return t('otp.stepsTable.freqThriceDaily', '3x/day');
            case 4: return t('otp.stepsTable.freqFourDaily', '4x/day');
            default: return `${freq}x/day`;
        }
    };

    const formatTimeOfDay = (time?: TimeOfDay): string => {
        if (!time) return '';
        switch (time) {
            case 'morning': return t('otp.stepsTable.timeOfDay.morning', 'Morning');
            case 'afternoon': return t('otp.stepsTable.timeOfDay.afternoon', 'Afternoon');
            case 'evening': return t('otp.stepsTable.timeOfDay.evening', 'Evening');
            case 'night': return t('otp.stepsTable.timeOfDay.night', 'Night');
            default: return '';
        }
    };

    const formatDateRange = (startDate: string, endDate: string): string => {
        const start = toLocalCalendarDate(startDate);
        const end = toLocalCalendarDate(endDate);
        if (!start || !end) return '-';

        const formatDate = (date: Date) => {
            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const day = date.getDate();
            return `${month} ${day}`;
        };

        return `${formatDate(start)} - ${formatDate(end)}`;
    };

    // Calculate actual MME from medications
    const calculateStepActualMME = (medications: IStepMedication[]): number | null => {
        if (!medications || medications.length === 0) return null;

        // Only calculate if all medications have conversion factors
        const hasAllFactors = medications.every((m) => m.conversionFactor !== undefined);
        if (!hasAllFactors) return null;

        const totalMME = medications.reduce((sum, med) => {
            const mme = med.dosageStrength * med.frequencyPerDay * (med.conversionFactor || 0);
            return sum + mme;
        }, 0);

        return parseFloat(totalMME.toFixed(1));
    };

    const getVarianceColor = (targetMME: number, actualMME: number): string => {
        const difference = Math.abs(targetMME - actualMME);
        if (difference < 5) return '#4caf50'; // Green
        if (difference < 10) return '#ff9800'; // Orange
        return '#f44336'; // Red
    };

    const getVarianceTooltipMessage = (targetMME: number, actualMME: number): string => {
        const difference = actualMME - targetMME;
        const absDifference = Math.abs(difference);

        if (absDifference < 5)
            return t('otp.stepsTable.varianceGood', 'Closely matches target (within 5 MME)');

        if (difference > 0)
            return t('otp.stepsTable.varianceExceeds', { diff: absDifference.toFixed(1) });

        return t('otp.stepsTable.varianceBelow', { diff: absDifference.toFixed(1) });
    };

    const handleMedicationSave = (stepIndex: number, medication: IStepMedication) => {
        const step = steps[stepIndex];
        const medications = step.medications ? [...step.medications] : [];

        // Check if editing existing medication
        const existingIndex = medications.findIndex((m) => m.id === medication.id);

        if (existingIndex >= 0)
            medications[existingIndex] = medication;
        else
            medications.push(medication);

        const updated = steps.map((s, i) =>
            i === stepIndex
                ? { ...s, medications, isManuallyEdited: true }
                : s
        );

        // Check if this is an edit with a drug/route change
        const oldMed = editingMedication?.medication;
        if (oldMed && (oldMed.drugID !== medication.drugID || oldMed.routeID !== medication.routeID)) {
            const hasMatchingSubsequentMeds = steps.some((s, i) =>
                i > stepIndex &&
                s.medications?.some((m) => m.drugID === oldMed.drugID && m.routeID === oldMed.routeID)
            );

            if (hasMatchingSubsequentMeds) {
                promptApplyDrugToSubsequentSteps(stepIndex, updated, oldMed, medication);
                return;
            }
        }

        onStepChange(updated);
    };

    const promptApplyDrugToSubsequentSteps = (
        stepIndex: number,
        updatedSteps: ITaperingStep[],
        oldMed: IStepMedication,
        newMed: IStepMedication,
    ) => {
        modals.openConfirmModal({
            title: t('otp.stepsTable.drugChangeTitle'),
            children: (
                <Text size="sm">
                    {t('otp.stepsTable.drugChangeConfirm')}
                </Text>
            ),
            labels: {
                confirm: t('otp.stepsTable.applyToAll'),
                cancel: t('otp.stepsTable.applyToThisOnly'),
            },
            onConfirm: () => {
                // Set customDrugID/customRouteID on subsequent steps and clear their
                // medications so the parent's dose suggestion useEffect re-populates
                // with correct virtual MME targets and proper step count adjustment.
                const finalSteps = updatedSteps.map((step, i) => {
                    if (i <= stepIndex) return step;

                    const hasOldDrug = step.medications?.some(
                        (m) => m.drugID === oldMed.drugID && m.routeID === oldMed.routeID
                    );
                    if (!hasOldDrug) return step;

                    return {
                        ...step,
                        customDrugID: newMed.drugID,
                        customRouteID: newMed.routeID,
                        medications: [],
                        isManuallyEdited: false,
                    };
                });
                onStepChange(finalSteps);
            },
            onCancel: () => onStepChange(updatedSteps),
        });
    };

    const handleMedicationDelete = (stepIndex: number, medicationId: string) => {
        const step = steps[stepIndex];
        const medications = (step.medications || []).filter((m) => m.id !== medicationId);

        const updated = steps.map((s, i) =>
            i === stepIndex
                ? { ...s, medications, isManuallyEdited: true }
                : s
        );
        onStepChange(updated);
    };

    const openAddMedication = (stepIndex: number) => {
        setEditingMedication({
            stepIndex,
            medicationIndex: null,
        });
    };

    const openEditMedication = (stepIndex: number, medicationIndex: number, medication: IStepMedication) => {
        setEditingMedication({
            stepIndex,
            medicationIndex,
            medication,
        });
    };

    // Generate a consistent color for a drug based on its ID
    const getDrugColor = (drugID: number): string => {
        const colors = [
            'teal',
            'blue',
            'violet',
            'grape',
            'pink',
            'orange',
            'cyan',
            'indigo',
            'lime',
            'yellow',
        ];
        return colors[drugID % colors.length];
    };

    if (steps.length === 0) {
        return (
            <Paper p="md" withBorder>
                <Text c="dimmed" ta="center">
                    {t('otp.stepsTable.noSteps')}
                </Text>
            </Paper>
        );
    }

    return (
        <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
            {/* Collapsed Header Bar */}
            <Flex
                p="md"
                justify="space-between"
                align="center"
                style={{
                    cursor: 'pointer',
                    backgroundColor: '#f9f9f9',
                    borderRadius: isExpanded ? '4px 4px 0 0' : 4,
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <Group gap="sm">
                    <Title order={5}>{t('otp.stepsTable.title')}</Title>
                    <Box style={{ width: 1, height: 24, backgroundColor: '#d0d0d0' }} />
                    <Badge
                        color={isCustomized ? 'orange' : 'blue'}
                        variant="light"
                        size="sm"
                    >
                        {isCustomized
                            ? t('otp.stepsTable.customized')
                            : t('otp.stepsTable.autoGenerated')}
                    </Badge>
                    <Text size="sm" c="dimmed">
                        ({steps.length} {t('otp.stepsTable.stepsCount', 'steps')})
                    </Text>
                </Group>
                <Flex gap="sm" align="center">
                    <Group gap="xs" onClick={(e) => e.stopPropagation()}>
                        {isCustomized && (
                            <Tooltip label={t('otp.stepsTable.resetToAuto')}>
                                <Button
                                    variant="subtle"
                                    size="xs"
                                    leftSection={<IconRefresh size={14} />}
                                    onClick={onReset}
                                    disabled={disabled}
                                >
                                    {t('otp.stepsTable.resetToAuto')}
                                </Button>
                            </Tooltip>
                        )}
                        {!disabled && (canUndo || canRedo) && (
                            <Group gap={4}>
                                <Tooltip label={t('otp.stepsTable.undo', 'Undo (Ctrl+Z)')}>
                                    <ActionIcon
                                        variant="subtle"
                                        size="sm"
                                        onClick={onUndo}
                                        disabled={!canUndo}
                                    >
                                        <IconArrowBackUp size={16} />
                                    </ActionIcon>
                                </Tooltip>
                                <Tooltip label={t('otp.stepsTable.redo', 'Redo (Ctrl+Shift+Z)')}>
                                    <ActionIcon
                                        variant="subtle"
                                        size="sm"
                                        onClick={onRedo}
                                        disabled={!canRedo}
                                    >
                                        <IconArrowForwardUp size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        )}
                        <Button
                            variant="light"
                            size="xs"
                            leftSection={<IconPlus size={14} />}
                            onClick={handleAddStep}
                            disabled={disabled}
                        >
                            {t('otp.stepsTable.addStep')}
                        </Button>
                    </Group>
                    <IconChevronDown
                        size={20}
                        style={{
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 300ms ease',
                        }}
                    />
                </Flex>
            </Flex>

            {/* Expanded Table Content */}
            <Collapse in={isExpanded} transitionDuration={300}>
                <Box p="md" style={{ borderTop: '1px solid #e0e0e0' }}>
                    <ScrollArea>
                <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th style={{ width: 60 }}>
                                {t('otp.stepsTable.step')}
                            </Table.Th>
                            <Table.Th style={{ width: 100 }}>
                                {t('otp.stepsTable.duration')}
                            </Table.Th>
                            <Table.Th style={{ width: 140 }}>
                                {t('otp.stepsTable.dateRange')}
                            </Table.Th>
                            <Table.Th style={{ width: 200 }}>
                                <Tooltip
                                    label={
                                        <Stack gap={6}>
                                            <Group gap={4} wrap="nowrap">
                                                <Text size="xs" fw={700} c="#64b5f6">{t('otp.stepsTable.headerPlanned', 'Planned')}:</Text>
                                                <Text size="xs">{t('otp.stepsTable.headerPlannedTooltip')}</Text>
                                            </Group>
                                            <Group gap={4} wrap="nowrap">
                                                <Text size="xs" fw={700} c="#ce93d8">{t('otp.stepsTable.headerTargeted', 'Targeted')}:</Text>
                                                <Text size="xs">{t('otp.stepsTable.headerTargetedTooltip')}</Text>
                                            </Group>
                                            <Group gap={4} wrap="nowrap">
                                                <Text size="xs" fw={700} c="#81c784">{t('otp.stepsTable.headerActual', 'Actual')}:</Text>
                                                <Text size="xs">{t('otp.stepsTable.headerActualTooltip')}</Text>
                                            </Group>
                                        </Stack>
                                    }
                                    multiline
                                    w={300}
                                    withArrow
                                    position="top"
                                >
                                    <Group gap={4} wrap="nowrap" style={{ cursor: 'help' }}>
                                        <Text size="sm" fw={700} c="#0066cc">{t('otp.stepsTable.headerPlanned', 'Planned')}</Text>
                                        <Text size="sm" fw={700} c="#9c27b0">({t('otp.stepsTable.headerTargeted', 'Targeted')})</Text>
                                        <Text size="sm" fw={700} c="dimmed">→</Text>
                                        <Text size="sm" fw={700} c="#2e7d32">{t('otp.stepsTable.headerActual', 'Actual')}</Text>
                                        <Text size="sm" fw={700}>MME</Text>
                                    </Group>
                                </Tooltip>
                            </Table.Th>
                            {hasDrugColumns && (
                                <Table.Th style={{ minWidth: 280 }}>
                                    {t('otp.stepsTable.medications', 'Medications')}
                                </Table.Th>
                            )}
                            {!disabled && (
                                <Table.Th style={{ width: 60 }}>
                                    {t('otp.stepsTable.actions')}
                                </Table.Th>
                            )}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {steps.map((step, index) => {
                            const medications = step.medications || [];
                            const actualMME = calculateStepActualMME(medications);

                            return (
                                <Table.Tr key={step.stepNumber}>
                                    {/* Step Number */}
                                    <Table.Td>
                                        <Badge
                                            variant="light"
                                            color="blue"
                                            size="lg"
                                            radius="xl"
                                            style={{
                                                minWidth: 36,
                                                height: 36,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                paddingLeft: 8,
                                                paddingRight: 8,
                                            }}
                                        >
                                            {step.stepNumber}
                                        </Badge>
                                    </Table.Td>

                                    {/* Duration */}
                                    <Table.Td>
                                        {disabled ? (
                                            <Text size="sm">
                                                {step.durationDays} {t('otp.stepsTable.days')}
                                            </Text>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <NumberInput
                                                    size="xs"
                                                    value={step.durationDays}
                                                    onChange={(val) =>
                                                        handleDurationChange(
                                                            index,
                                                            Number(val) || 1,
                                                        )
                                                    }
                                                    min={1}
                                                    max={90}
                                                    step={1}
                                                    styles={{
                                                        input: {
                                                            textAlign: 'center',
                                                            width: 60,
                                                        },
                                                    }}
                                                />
                                                <Text size="xs" c="dimmed">
                                                    {t('otp.stepsTable.days')}
                                                </Text>
                                            </div>
                                        )}
                                    </Table.Td>

                                    {/* Date Range */}
                                    <Table.Td>
                                        <Text size="sm" c="dimmed">
                                            {formatDateRange(step.startDate, step.endDate)}
                                        </Text>
                                    </Table.Td>

                                    {/* Target → Actual MME */}
                                    <Table.Td>
                                        {(() => {
                                            const virtual = virtualMMEArray?.[index];
                                            const plannedMME = Math.round(step.doseMME * 2) / 2;
                                            const hasVirtualDiff = virtual !== undefined && Math.abs(virtual - plannedMME) >= 0.01;
                                            const displayVirtual = hasVirtualDiff ? virtual : plannedMME;
                                            const varianceTarget = hasVirtualDiff ? virtual : plannedMME;
                                            return (
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: anyVirtualDiff
                                                        ? '50px 58px 24px 42px 12px'
                                                        : '50px 24px 42px 12px',
                                                    alignItems: 'center',
                                                }}>
                                                    <Text size="sm" fw={500} c="#0066cc">
                                                        {plannedMME}
                                                    </Text>
                                                    {anyVirtualDiff && (
                                                        <Tooltip
                                                            label={hasVirtualDiff
                                                                ? t('otp.stepsTable.virtualMMETooltip', 'Adjusted target from manual edits on previous steps')
                                                                : t('otp.stepsTable.virtualMMESameTooltip', 'Target matches planned MME')
                                                            }
                                                            withArrow
                                                            position="top"
                                                        >
                                                            <Text size="xs" c={hasVirtualDiff ? '#9c27b0' : 'dimmed'} style={{ cursor: 'help' }}>
                                                                ({displayVirtual})
                                                            </Text>
                                                        </Tooltip>
                                                    )}
                                                    {actualMME !== null ? (
                                                        <Text size="sm" c="dimmed">→</Text>
                                                    ) : <span />}
                                                    {actualMME !== null ? (
                                                        <Text size="sm" fw={500} c="#2e7d32">
                                                            {actualMME}
                                                        </Text>
                                                    ) : <span />}
                                                    {actualMME !== null ? (
                                                        <Tooltip
                                                            label={getVarianceTooltipMessage(varianceTarget, actualMME)}
                                                            withArrow
                                                            position="top"
                                                        >
                                                            <Box
                                                                style={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: getVarianceColor(varianceTarget, actualMME),
                                                                    cursor: 'help',
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ) : <span />}
                                                </div>
                                            );
                                        })()}
                                    </Table.Td>

                                    {/* Medications (Chip/Tag System) */}
                                    {hasDrugColumns && (
                                        <Table.Td>
                                            <Group gap="xs" wrap="wrap">
                                                {medications.map((med, medIndex) => (
                                                    <Tooltip
                                                        key={med.id}
                                                        label={
                                                            <Stack gap={2}>
                                                                <Text size="xs" fw={500}>{med.drugName}</Text>
                                                                <Text size="xs">
                                                                    {med.dosageStrength}{med.unitName} - {formatFrequency(med.frequencyPerDay)}
                                                                </Text>
                                                                {med.timeOfDay && (
                                                                    <Text size="xs" c="dimmed">
                                                                        {formatTimeOfDay(med.timeOfDay)}
                                                                    </Text>
                                                                )}
                                                                {med.notes && (
                                                                    <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                                                                        {med.notes}
                                                                    </Text>
                                                                )}
                                                            </Stack>
                                                        }
                                                        multiline
                                                        w={220}
                                                    >
                                                        <Badge
                                                            variant="filled"
                                                            color={getDrugColor(med.drugID)}
                                                            size="md"
                                                            style={{ cursor: disabled ? 'default' : 'pointer' }}
                                                            onClick={disabled ? undefined : () => openEditMedication(index, medIndex, med)}
                                                            rightSection={
                                                                !disabled && (
                                                                    <ActionIcon
                                                                        size="xs"
                                                                        variant="transparent"
                                                                        color="white"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleMedicationDelete(index, med.id);
                                                                        }}
                                                                    >
                                                                        <IconX size={12} />
                                                                    </ActionIcon>
                                                                )
                                                            }
                                                        >
                                                            {med.drugName?.split(' ')[0]} {med.dosageStrength}{med.unitName} - {formatFrequency(med.frequencyPerDay)}
                                                            {med.timeOfDay && ` (${formatTimeOfDay(med.timeOfDay).substring(0, 3)})`}
                                                        </Badge>
                                                    </Tooltip>
                                                ))}

                                                {/* Add medication button */}
                                                {!disabled && (
                                                    <Tooltip label={t('otp.stepsTable.addMedicationBtn', 'Add medication')}>
                                                        <ActionIcon
                                                            variant="light"
                                                            color="blue"
                                                            size="sm"
                                                            onClick={() => openAddMedication(index)}
                                                        >
                                                            <IconPlus size={14} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}

                                                {/* Show hint when no medications */}
                                                {medications.length === 0 && (
                                                    <Text size="xs" c="dimmed">
                                                        {t('otp.stepsTable.noMedications', 'No medications added')}
                                                    </Text>
                                                )}
                                            </Group>
                                        </Table.Td>
                                    )}

                                    {/* Actions */}
                                    {!disabled && (
                                        <Table.Td>
                                            <Tooltip label={t('otp.stepsTable.removeStep')}>
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="red"
                                                    size="sm"
                                                    onClick={() => handleRemoveStep(index)}
                                                    disabled={steps.length <= 1}
                                                >
                                                    <IconTrash size={14} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </Table.Td>
                                    )}
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
                    </ScrollArea>
                </Box>
            </Collapse>

            {/* Medication Edit Modal */}
            {editingMedication !== null && drugRouteOptions && (
                <MedicationEditModal
                    opened={editingMedication !== null}
                    onClose={() => setEditingMedication(null)}
                    stepNumber={steps[editingMedication.stepIndex].stepNumber}
                    medication={editingMedication.medication}
                    suggestion={doseSuggestions?.[editingMedication.stepIndex]}
                    drugRouteOptions={drugRouteOptions}
                    globalDrugRouteKey={globalDrugRouteKey}
                    onSave={(med) => handleMedicationSave(editingMedication.stepIndex, med)}
                    onDelete={
                        editingMedication.medication
                            ? () => handleMedicationDelete(
                                editingMedication.stepIndex,
                                editingMedication.medication!.id
                            )
                            : undefined
                    }
                    isNew={editingMedication.medicationIndex === null}
                />
            )}
        </Box>
    );
};

export default TaperingStepsTable;
